import { useState, useCallback } from 'react';
import useAuthToken from './useAuthToken';
import { getApiBaseUrl } from '../utils/apiConfig';

/**
 * Hook for secure document access with authentication and tenant isolation
 */
export const useSecureDocuments = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const token = useAuthToken();

  /**
   * Get secure access token for a document
   * @param {string} documentId - Document ID to access
   * @returns {Promise<{secure_url: string, expires_in: number, message: string}>}
   */
  const getSecureAccess = useCallback(async (documentId) => {
    if (!documentId) {
      throw new Error('Document ID is required');
    }

    setLoading(true);
    setError(null);

    try {
      if (!token) {
        throw new Error('Authentication required to access documents');
      }

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };



      const response = await fetch(`${getApiBaseUrl()}/documents/${documentId}/access`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication failed. Please log in again.');
        } else if (response.status === 403) {
          throw new Error('Access denied. You do not have permission to view this document.');
        } else if (response.status === 404) {
          throw new Error('Document not found.');
        } else {
          throw new Error(`Failed to get document access: ${response.statusText}`);
        }
      }

      const data = await response.json();
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [token]);

  /**
   * Open a document securely in a new tab/window
   * @param {string} documentId - Document ID to open
   * @param {boolean} newTab - Whether to open in new tab (default: true)
   */
  const openSecureDocument = useCallback(async (documentId, newTab = true) => {
    try {
      const accessData = await getSecureAccess(documentId);
      // token is already available from useAuthToken() at hook level
      
      // Make authenticated request to get the document
      const documentUrl = accessData.secure_url.startsWith('http') 
        ? accessData.secure_url 
        : `${window.location.origin}${accessData.secure_url}`;

      const headers = {
        'Authorization': `Bearer ${token}`,
      };

      // Add Vercel protection bypass header if available


      const response = await fetch(documentUrl, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch document: ${response.statusText}`);
      }

      // Get the blob and create object URL
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      if (newTab) {
        // Open in new tab for viewing (not downloading)
        const newWindow = window.open('', '_blank');
        if (newWindow) {
          // Create a simple HTML page to display the document
          const fileExtension = accessData.filename ? accessData.filename.split('.').pop().toLowerCase() : '';
          const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(fileExtension);
          const isPdf = fileExtension === 'pdf';
          
          if (isImage) {
            newWindow.document.write(`
              <html>
                <head><title>${accessData.filename || 'Document'}</title></head>
                <body style="margin:0; display:flex; justify-content:center; align-items:center; min-height:100vh; background:#f0f0f0;">
                  <img src="${blobUrl}" style="max-width:100%; max-height:100vh; object-fit:contain;" alt="Document" />
                </body>
              </html>
            `);
          } else if (isPdf) {
            newWindow.document.write(`
              <html>
                <head><title>${accessData.filename || 'Document'}</title></head>
                <body style="margin:0;">
                  <embed src="${blobUrl}" type="application/pdf" width="100%" height="100%" />
                </body>
              </html>
            `);
          } else {
            // For other file types, navigate directly to the blob URL
            newWindow.location.href = blobUrl;
          }
          newWindow.document.close();
        } else {
          // Fallback if popup blocked - download instead
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = accessData.filename || 'document';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      } else {
        // Replace current window
        window.location.href = blobUrl;
      }

      // Clean up blob URL after a delay to allow browser to load it
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
    } catch (err) {
      console.error('Failed to open secure document:', err);
      throw err;
    }
  }, [getSecureAccess, token]);

  /**
   * Download a document securely
   * @param {string} documentId - Document ID to download
   * @param {string} filename - Optional filename for download
   */
  const downloadSecureDocument = useCallback(async (documentId, filename = null) => {
    try {
      const accessData = await getSecureAccess(documentId);
      // token is already available from useAuthToken() at hook level
      
      // Make authenticated request to get the document
      const documentUrl = accessData.secure_url.startsWith('http') 
        ? accessData.secure_url 
        : `${window.location.origin}${accessData.secure_url}`;

      const headers = {
        'Authorization': `Bearer ${token}`,
      };



      const response = await fetch(documentUrl, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch document: ${response.statusText}`);
      }

      // Get the blob and create download
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      
      // Create a temporary anchor element for download
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename || accessData.filename || 'document';
      
      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up blob URL
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (err) {
      console.error('Failed to download secure document:', err);
      throw err;
    }
  }, [getSecureAccess, token]);

  /**
   * Get legacy document URL (deprecated - for backward compatibility only)
   * @param {string} documentId - Document ID
   * @returns {Promise<{url: string, source: string}>}
   * @deprecated Use getSecureAccess instead
   */
  const getLegacyDocumentUrl = useCallback(async (documentId) => {
    console.warn('DEPRECATED: getLegacyDocumentUrl is deprecated. Use getSecureAccess instead for better security.');
    
    setLoading(true);
    setError(null);

    try {
      // token is already available from useAuthToken() at hook level
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(`${getApiBaseUrl()}/documents/${documentId}/view`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to get document URL: ${response.statusText}`);
      }

      return await response.json();
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [token]);

  return {
    loading,
    error,
    getSecureAccess,
    openSecureDocument,
    downloadSecureDocument,
    getLegacyDocumentUrl, // deprecated
  };
};

export default useSecureDocuments; 